import { Box, Grid } from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { TotalProperty } from 'src/total/components/total';
import { styles } from 'src/total/components/total-styles';
import VerticalCenter from 'src/vertical-center/vertical-center';
import { itemCount } from './service/item-count-helper';

interface ReturnModeTotalProps {
  subTotal: number;
  tax: number;
  savings: number;
  ejItems: IElectronicJournalRecord[];
  currencyCode: string;
  trainingModeActive: boolean;
}

const ReturnsTotal: React.FC<ReturnModeTotalProps> = (props: ReturnModeTotalProps): JSX.Element => {
  const classes = styles();
  const trainingModeClasses = trainingModeStyles();
  const { t } = useTranslation();
  const { subTotal, tax, savings, ejItems, currencyCode, trainingModeActive } = props;

  const hasProductItem = ejItems.filter(i => i.Type === EJRecordTypes.Product).length > 0;
  const hasReturnItem = ejItems.filter(i => i.Type === EJRecordTypes.ReturnItem).length > 0;

  return (
    <Grid container direction='column' className={`${classes.outerContainer}`}>
      <Grid item className={`fullWidth fullHeight`}>
        <Grid
          container
          className={`${classes.container} ${
            trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
          }`}
        >
          <Grid item className={`flexFull ${classes.totalContent}`}>
            <VerticalCenter itemClass='fullWidth'>
              <Box className={classes.totalRow}>
                <Grid container>
                  <Grid item xs={5}>
                    <TotalProperty
                      label={t('ItemDetailsView_SaleItems').toUpperCase()}
                      value={itemCount(ejItems, EJRecordTypes.Product).toString()}
                      classes={{ left: classes.totalLeft, right: classes.totalRight }}
                      columns={{ left: 7, right: 5 }}
                      dataTestID='items-total'
                    />
                  </Grid>
                  <Grid item xs={1} />
                  <Grid item xs={6}>
                    <TotalProperty
                      label={t('ItemDetailsView_Savings').toUpperCase()}
                      value={convertCurrency(savings, currencyCode)}
                      classes={{ left: classes.totalLeft, right: classes.totalRight }}
                      columns={{ left: 7, right: 5 }}
                      dataTestID='items-savings'
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box className={classes.totalRow}>
                <Grid container>
                  <Grid item xs={5}>
                    <TotalProperty
                      label={t('ItemDetailsView_ReturnItems').toUpperCase()}
                      value={itemCount(ejItems, EJRecordTypes.ReturnItem).toString()}
                      classes={{ left: classes.totalLeft, right: classes.totalRight }}
                      columns={{
                        left: hasProductItem && hasReturnItem ? 9 : 7,
                        right: hasProductItem && hasReturnItem ? 3 : 5,
                      }}
                      dataTestID='return-items-total'
                    />
                  </Grid>
                  <Grid item xs={1} />
                  <Grid item xs={6}>
                    <TotalProperty
                      label={t('ItemDetailsView_SubTotal').toUpperCase()}
                      value={convertCurrency(subTotal, currencyCode)}
                      classes={{ left: classes.totalLeft, right: classes.totalRight }}
                      dataTestID='items-subtotal'
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box>
                <Grid container>
                  <Grid item xs={6} />
                  <Grid item xs={6}>
                    <TotalProperty
                      label={t('ReceiptView_Tax').toUpperCase()}
                      value={convertCurrency(tax, currencyCode)}
                      classes={{ left: classes.totalLeft, right: classes.totalRight }}
                      dataTestID='items-tax'
                    />
                  </Grid>
                </Grid>
              </Box>
            </VerticalCenter>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state: AppState): ReturnModeTotalProps => ({
  subTotal: state.itemList.subTotal,
  tax: state.itemList.tax,
  savings: state.itemList.savings,
  ejItems: state.itemList.ejItems,
  currencyCode: state.itemList.currencyCode,
  trainingModeActive: state.posMode.trainingMode,
});

export default connect(mapStateToProps)(ReturnsTotal);
